/**
 * Partial-patch plumbing for the Settings page.
 *
 * WHY THIS EXISTS
 * ---------------
 * `PATCH /api/settings/:category` is a *partial* update: the server merges the
 * body over the stored value (`packages/api/src/utils/deepMerge.ts`) before
 * validating, precisely because Zod `.default()` + a body-replacing `validate`
 * middleware once turned a one-field PATCH into a whole-category reset. Observed
 * live on 2026-07-30: a settings PATCH carrying only `requirePhoto` reset
 * `gpsAccuracyThresholdMeters` 250 → 50 and `mockLocationMode` "flag" → "reject".
 *
 * The server-side merge fixed the blast radius, but a client that keeps POSTing
 * the WHOLE category still re-asserts every field on every save — so a value
 * another admin (or a migration, or the install wizard) changed between this
 * page's GET and its PATCH is silently stamped back to whatever this tab last
 * read. That is the same defect wearing a different hat.
 *
 * So the page never sends a whole object. It tracks which *paths* the user
 * touched and `buildPatch` assembles the minimal nested body for exactly those.
 * Change `requirePhoto.web` and the request body is `{"requirePhoto":{"web":true}}`
 * — one key, nothing else asserted.
 */

/** True only for `{}`-style objects — not arrays, Dates or null. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value) as object | null;
  return proto === Object.prototype || proto === null;
}

/**
 * Read a dot-path out of a nested value. An empty path means "the value
 * itself", which is how the array-valued `reportSchedules` category is
 * addressed. Returns `undefined` for a path that doesn't resolve.
 */
export function readPath(source: unknown, path: string): unknown {
  if (path === '') return source;
  let cursor: unknown = source;
  for (const segment of path.split('.')) {
    if (!isPlainObject(cursor)) return undefined;
    cursor = cursor[segment];
  }
  return cursor;
}

/**
 * Return a structurally-shared copy of `source` with `path` set to `value`.
 * Intermediate objects are cloned (never mutated) so React sees a new
 * reference, and a missing intermediate is created rather than throwing.
 */
export function writePath<T>(source: T, path: string, value: unknown): T {
  if (path === '') return value as T;
  const [head, ...rest] = path.split('.');
  const key = head as string;
  const base: Record<string, unknown> = isPlainObject(source) ? { ...source } : {};
  base[key] =
    rest.length === 0 ? value : writePath(base[key] ?? {}, rest.join('.'), value);
  return base as T;
}

/**
 * Structural equality, sufficient for settings values: scalars, `null`, string
 * and number arrays, and the shallow objects the schema nests. Used to decide
 * whether a field is still dirty after an edit round-trips back to its original.
 */
export function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => sameValue(item, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return (
      ka.length === kb.length && ka.every((k) => k in b && sameValue(a[k], b[k]))
    );
  }
  return false;
}

/**
 * Build the minimal PATCH body for `paths`, read out of `draft`.
 *
 * Only the named paths appear. Sibling keys are absent — not `undefined`, not
 * re-sent at their current value — so the server's deepMerge keeps whatever is
 * stored for everything the admin didn't touch.
 *
 * The empty path is special-cased for `reportSchedules`, whose category value is
 * an array: deepMerge replaces arrays wholesale, which is the only sensible
 * semantics for a list (sending `[a]` means "the list is now just a", never
 * "append a").
 */
export function buildPatch(
  draft: unknown,
  paths: readonly string[]
): unknown {
  if (paths.includes('')) return draft;
  let patch: Record<string, unknown> = {};
  for (const path of paths) {
    patch = writePath(patch, path, readPath(draft, path));
  }
  return patch;
}

/**
 * Count the leaf keys in a patch body. Used by the tests as the regression
 * guard for the destructive-PATCH defect, and by the save bar to say how many
 * values are actually going over the wire.
 */
export function countLeaves(patch: unknown): number {
  if (!isPlainObject(patch)) return 1;
  return Object.values(patch).reduce<number>(
    (sum, value) => sum + countLeaves(value),
    0
  );
}
